import { Component, OnInit, OnDestroy } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { CookieConsentService } from 'src/app/services/cookie-consent/cookie-consent.service';
import { Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { FormsModule } from '@angular/forms';

type ThirdPartyProvider = 'google' | 'apple' | 'facebook';

interface ThirdPartyProviderOption {
  id: ThirdPartyProvider;
  labelKey: string;
}

interface CookieConsent {
  technical: true;
  providers: Record<ThirdPartyProvider, boolean>;
}

const COOKIE_CONSENT_KEY = 'cookie_consent';

@Component({
  selector: 'app-cookie-banner',
  templateUrl: './cookie-banner.component.html',
  styleUrls: ['./cookie-banner.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, TranslateModule]
})
export class CookieBannerComponent implements OnInit {
  showBanner = false;
  showConfig = false;
  hasStoredConsent = false;

  // animation / visibility helpers
  visibleBanner = false; // controls *ngIf for banner
  bannerAnimationClass: string | null = null;
  // timeout handle for delayed show
  private bannerShowTimeout?: any;

  visibleConfig = false; // controls *ngIf for overlay
  overlayAnimationClass: string | null = null;
  overlayVisibleClass: string | null = null;
  modalAnimationClass: string | null = null;
  isWebContext = false;

  consent: CookieConsent = this.createDefaultConsent();
  readonly thirdPartyProviders: ThirdPartyProviderOption[] = [
    { id: 'google', labelKey: 'COOKIE_BANNER.THIRD_PARTY.PROVIDERS.GOOGLE' },
    { id: 'apple', labelKey: 'COOKIE_BANNER.THIRD_PARTY.PROVIDERS.APPLE' },
    { id: 'facebook', labelKey: 'COOKIE_BANNER.THIRD_PARTY.PROVIDERS.FACEBOOK' },
  ];

  technicalPurposeList: string[] = [];
  technicalOwnList: string[] = [];
  technicalStripeList: string[] = [];
  technicalExampleList: string[] = [];

  private openConfigSub?: Subscription;

  constructor(
    private utilsService: UtilsService,
    private consentService: CookieConsentService,
    private translateService: TranslateService
  ) { }

  async ngOnInit() {
    try {
      this.isWebContext = await this.utilsService.detectIsWeb();
    } catch (error) {
      this.isWebContext = false;
    }

    if (this.isWebContext) {
      await this.loadStoredConsent();
    }

    this.loadTranslatedList('COOKIE_BANNER.TECHNICAL.OWN_LIST', (items) => {
      this.technicalOwnList = items;
    });
    this.loadTranslatedList('COOKIE_BANNER.TECHNICAL.STRIPE_LIST', (items) => {
      this.technicalStripeList = items;
    });
    this.loadTranslatedList('COOKIE_BANNER.TECHNICAL.EXAMPLES_LIST', (items) => {
      this.technicalExampleList = items;
      this.technicalPurposeList = items;
    });

    // Listen for requests to open the cookie configuration panel (from other components)
    this.openConfigSub = this.consentService.openConfig$.subscribe(() => {
      // only open in web
      if (this.isWebContext) {
        this.showBanner = false;
        // Cancel pending banner show if scheduled
        if (this.bannerShowTimeout) {
          clearTimeout(this.bannerShowTimeout);
          this.bannerShowTimeout = undefined;
        }
        this.showConfigAnimated();
      }
    });
  }

  private createDefaultConsent(): CookieConsent {
    return {
      technical: true,
      providers: {
        google: false,
        apple: false,
        facebook: false,
      },
    };
  }

  private loadTranslatedList(key: string, assign: (items: string[]) => void) {
    try {
      this.translateService.get(key).subscribe((value: unknown) => {
        assign(this.normalizeTranslatedList(value));
      });
    } catch (error) {
      assign([]);
    }
  }

  private normalizeTranslatedList(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string');
    }

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);

        return Array.isArray(parsed)
          ? parsed.filter((item): item is string => typeof item === 'string')
          : [value];
      } catch {
        return [value];
      }
    }

    return [];
  }

  private async loadStoredConsent() {
    try {
      const { value } = await Preferences.get({ key: COOKIE_CONSENT_KEY });

      if (!value) {
        this.hasStoredConsent = false;

        this.bannerShowTimeout = setTimeout(() => {
            this.showBannerAnimated();
        }, 5000);        
        return;
      }

      const stored = JSON.parse(value) as CookieConsent;
      this.hasStoredConsent = true;
      this.consent = {
        ...this.createDefaultConsent(),
        ...stored,
        providers: {
          ...this.createDefaultConsent().providers,
          ...(stored?.providers ?? {}),
        },
      };

      this.applyConsent();
    } catch (error) {
      this.hasStoredConsent = false;
      this.consent = this.createDefaultConsent();
      this.showBanner = true;
    }
  }

  openConfig() {
    // show overlay and modal with enter animations
    this.showConfigAnimated();
  }

  closeConfig() {
    // play shrink animations then hide
    this.hideConfigAnimated();
  }

  async acceptAll() {
    this.setAllProviders(true);
    await this.persistConsent();
  }

  async rejectAll() {
    this.setAllProviders(false);
    await this.persistConsent();
  }

  async saveConfig() {
    await this.persistConsent();
  }

  private setAllProviders(value: boolean) {
    (Object.keys(this.consent.providers) as ThirdPartyProvider[]).forEach((provider) => {
      this.consent.providers[provider] = value;
    });
  }

  private async persistConsent() {
    await Preferences.set({
      key: COOKIE_CONSENT_KEY,
      value: JSON.stringify(this.consent),
    });
    this.hasStoredConsent = true;
    // hide with animations
    this.hideBannerAnimated();
    this.hideConfigAnimated();
    this.applyConsent();
    // Persist via service as well
    try { await this.consentService.persistConsent(this.consent as any); } catch {}
  }

  // Animation helpers
  private showBannerAnimated() {
    this.visibleBanner = true;
    // small delay to ensure class binding applies after insertion
    setTimeout(() => {
      this.bannerAnimationClass = 'slide-in-up';
    }, 10);
  }

  private hideBannerAnimated() {
    // if not visible, nothing to do
    if (!this.visibleBanner) return;
    this.bannerAnimationClass = 'slide-out-down';
    // after animation duration hide element
    setTimeout(() => {
      this.visibleBanner = false;
      this.bannerAnimationClass = null;
    }, 300);
  }

  private showConfigAnimated() {
    this.visibleConfig = true;
    // overlay fade + modal expand
    // make overlay immediately interactive/visible to prevent flicker
    // apply visible helper class synchronously so the overlay is immediately
    // visible and can accept pointer events. Apply animation classes
    // immediately so the modal expand animation begins without a visual gap.
    this.overlayVisibleClass = 'visible';
    this.overlayAnimationClass = 'overlay-fade-in';
    this.modalAnimationClass = 'expand-in';
  }

  private hideConfigAnimated() {
    if (!this.visibleConfig) return;
    this.overlayAnimationClass = 'overlay-fade-out';
    this.modalAnimationClass = 'shrink-out';
    setTimeout(() => {
      this.visibleConfig = false;
      this.overlayAnimationClass = null;
      this.modalAnimationClass = null;
      this.overlayVisibleClass = null;
    }, 300);
  }

  private applyConsent() {
    if (!this.isWebContext) {
      return;
    }

    if (this.shouldEnableThirdPartyScripts()) {
      this.initializeThirdPartyScripts();
    } else {
      this.disableThirdPartyScripts();
    }
  }

  private shouldEnableThirdPartyScripts(): boolean {
    return Object.values(this.consent.providers).some((value) => value);
  }

  initializeThirdPartyScripts() {
  }

  disableThirdPartyScripts() {
  }

  shouldShowFooterLink(): boolean {
    return this.isWebContext && this.hasStoredConsent && !this.visibleBanner && !this.visibleConfig;
  }

  ngOnDestroy(): void {
    this.openConfigSub?.unsubscribe();
    if (this.bannerShowTimeout) {
      clearTimeout(this.bannerShowTimeout);
      this.bannerShowTimeout = undefined;
    }
  }
}
